import { api } from './api'
import { getAll } from './pagination'

export interface AttendanceRecord {
  id: number
  student: number
  student_name: string
  student_id: string
  class_name: string
  date: string
  status: 'present' | 'absent' | 'late' | 'excused' | 'half_day' | 'sick' | 'emergency'
  period: 'morning' | 'afternoon' | 'full_day' | 'evening'
  timetable_slot?: number | null
  lesson_subject_name?: string | null
  lesson_class_name?: string | null
  lesson_period_name?: string | null
  time_in?: string
  time_out?: string
  remarks?: string
  marked_by: number
  marked_by_name: string
  is_verified: boolean
  verification_notes?: string
  parent_notified: boolean
  notification_sent_at?: string
  is_absent: boolean
  is_present: boolean
  attendance_score: number
  created_at: string
  updated_at: string
}

export interface AttendanceAnalytics {
  period: {
    start_date: string
    end_date: string
    days: number
  }
  overall_stats: {
    total_records: number
    present_count: number
    absent_count: number
    overall_percentage: number
  }
  daily_trends: Array<{
    date: string
    present: number
    total: number
    percentage: number
  }>
  class_statistics: Array<{
    class_name: string
    present: number
    total: number
    percentage: number
  }>
  poor_attendance_students: Array<{
    id: number
    student_id: string
    student_name: string
    class_name: string
    percentage: number
    present_days: number
    total_days: number
  }>
}

export interface StudentAttendanceAnalytics {
  student: {
    id: number
    student_id: string
    name: string
    class: string
  }
  period: {
    start_date: string
    end_date: string
    days: number
  }
  statistics: {
    total_days: number
    present_days: number
    absent_days: number
    late_days: number
    excused_days: number
    attendance_percentage: number
  }
  monthly_breakdown: Array<{
    month: string
    present_days: number
    total_days: number
    percentage: number
  }>
  recent_attendance: AttendanceRecord[]
}

export interface AttendanceAlert {
  id: number
  student: number
  student_pk: number
  student_name: string
  student_id: string
  class_name: string
  alert_type: 'low_attendance' | 'frequent_absences' | 'pattern_alert' | 'consecutive_absences'
  priority: 'low' | 'medium' | 'high' | 'critical'
  message: string
  attendance_percentage: number | string
  period_start: string
  period_end: string
  is_resolved: boolean
  resolved_at?: string
  resolved_by?: number
  resolved_by_name?: string
  resolution_notes?: string
  parent_notified: boolean
  created_at: string
  updated_at: string
}

export interface RegisterLearner {
  id: number
  student_id: string
  full_name: string
  class_name?: string
}

export interface MarkAttendanceData {
  student_id: number
  date: string
  status: string
  period?: string
  timetable_slot?: number
  time_in?: string
  time_out?: string
  notes?: string
}

export interface BulkAttendanceData {
  timetable_slot?: number
  attendance_records: Array<{
    student_id: number
    date: string
    status: string
    period?: string
    time_in?: string
    time_out?: string
    notes?: string
  }>
}

class AttendanceService {
  // Get attendance records
  async getAttendanceRecords(params?: {
    student?: number
    current_class?: number
    date?: string
    date_from?: string
    date_to?: string
    /** A lesson's id, or "day" for the whole-day registers. */
    lesson?: string
    status?: string
    period?: string
  }) {
    return getAll<AttendanceRecord>('/students/attendance/', params)
  }

  // Get attendance analytics
  async getAttendanceAnalytics(days: number = 30): Promise<AttendanceAnalytics> {
    const response = await api.get('/students/attendance/analytics/', {
      params: { days }
    })
    return response.data
  }

  // Get student-specific attendance analytics
  async getStudentAttendanceAnalytics(
    studentId: number,
    days: number = 90
  ): Promise<StudentAttendanceAnalytics> {
    const response = await api.get(`/students/students/${studentId}/attendance/analytics/`, {
      params: { days }
    })
    return response.data
  }

  // Get student attendance records
  async getStudentAttendance(studentId: number) {
    const response = await api.get(`/students/students/${studentId}/attendance/`)
    return response.data
  }

  // Mark attendance for a student
  async markAttendance(data: MarkAttendanceData): Promise<AttendanceRecord> {
    const response = await api.post('/students/mark-attendance/', data)
    return response.data
  }

  // Bulk mark attendance
  async bulkMarkAttendance(data: BulkAttendanceData) {
    const response = await api.post('/students/attendance/bulk/', data)
    return response.data
  }

  // Get attendance alerts; the resolved ones too with include_resolved "1"
  async getAttendanceAlerts(params?: {
    priority?: string
    alert_type?: string
    include_resolved?: string
  }): Promise<AttendanceAlert[]> {
    return (await api.get('/students/attendance/alerts/', { params })).data
  }

  // Change what a saved record says (status, part of the day, times, remarks)
  async updateRecord({ id, data }: { id: number; data: Partial<Pick<AttendanceRecord, 'status' | 'period' | 'time_in' | 'time_out' | 'remarks'>> }): Promise<AttendanceRecord> {
    return (await api.patch(`/students/attendance/${id}/`, data)).data
  }

  // Everyone in a class, for its register (the learner directory, all pages)
  async getClassLearners(classId: number) {
    return getAll<RegisterLearner>('/students/service-directory/', { current_class: classId })
  }

  // The classes this person may take registers for
  async getRegisterClasses() {
    return getAll<{ id: number; name: string; level?: string; level_order?: number }>('/students/classes/')
  }

  // Resolve attendance alert
  async resolveAttendanceAlert(alertId: number, resolutionNotes: string) {
    const response = await api.post(`/students/attendance/alerts/${alertId}/resolve/`, {
      resolution_notes: resolutionNotes
    })
    return response.data
  }

  // Notify a learner's parent about poor attendance (the "below 80%" list)
  async notifyPoorAttendance(studentId: string): Promise<{
    notified: boolean
    student_id: string
    percentage: number
    present_days: number
    total_days: number
  }> {
    const response = await api.post(`/students/attendance/notify-poor-attendance/${studentId}/`)
    return response.data
  }

  // Get attendance statistics
  async getAttendanceStats() {
    const response = await api.get('/students/stats/')
    return response.data
  }

  // Get student statistics (for parent/student view)
  async getStudentStats() {
    const response = await api.get('/students/student-stats/')
    return response.data
  }
}

export const attendanceService = new AttendanceService()
